// The pure logic of the video studio: when things happen, how the camera moves, how subtitles are timed. No drawing here.

export const TRANSITION = 0.7; // seconds two neighbouring parts of the video overlap
export const MOTIONS = ["zoom-in", "pan-right", "zoom-out", "pan-left", "rise", "drift"];
export const MOTION_LABELS = { auto: "Automatic (varied)", "zoom-in": "Slow zoom in", "zoom-out": "Slow zoom out", "pan-left": "Pan left", "pan-right": "Pan right", rise: "Rise (tilt up)", drift: "Drift" };
export const TRANSITION_LABELS = { fade: "Cross-fade", dip: "Dip to black", slide: "Slide", wipe: "Wipe", zoom: "Zoom through", cut: "Cut (no overlap)" };

export const easeInOut = (x) => 0.5 - Math.cos(Math.PI * Math.min(1, Math.max(0, x))) / 2;
export const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

// The parts of the video in order: an optional title card, the scenes, an optional closing card. Each part overlaps the one before it by TRANSITION
// (a scene whose transition is "cut" does not overlap the scene before it: a person who goes on speaking is never cross-faded with themselves).
export function buildTimeline(scenes, { title = "", titleSub = "", outro = "", outroSub = "", introSeconds = 2.8, outroSeconds = 3.2 } = {}) {
  const items = [];
  const add = (item) => {
    const before = items[items.length - 1];
    const overlap = item.type === "scene" && item.scene.transition === "cut" && before && before.type === "scene" ? 0 : TRANSITION;
    const start = before ? before.end - overlap : 0;
    items.push({ ...item, overlap, start, end: start + item.duration });
  };
  if (title.trim()) add({ type: "title", text: title.trim(), sub: titleSub.trim(), duration: introSeconds });
  scenes.forEach((scene, i) => add({ type: "scene", scene, sceneIndex: i, duration: Math.max(1.5, Number(scene.seconds) || 4) }));
  if (outro.trim()) add({ type: "outro", text: outro.trim(), sub: outroSub.trim(), duration: outroSeconds });
  return { items, total: items.length ? items[items.length - 1].end : 0 };
}

// When a scene's narration starts, in video time: just after the transition into the scene has mostly passed.
export const narrationStart = (item, first) => item.start + (first ? 0.35 : (item.overlap ?? TRANSITION) * 0.6 + (item.overlap === 0 ? 0.1 : 0));

// The part that is on screen at time t, the part before it while the two overlap, and how far the change has gone (0..1, eased).
export function frameAt(timeline, t) {
  const { items } = timeline;
  let index = 0;
  for (let i = 0; i < items.length; i += 1) if (items[i].start <= t + 1e-9) index = i;
  const current = items[index];
  const overlap = current.overlap ?? TRANSITION;
  const previous = index > 0 && overlap > 0 && t < current.start + overlap ? items[index - 1] : null;
  const progress = previous ? easeInOut((t - current.start) / overlap) : 1;
  return { current, previous, progress, index };
}

// Where the camera is at u (0..1 through a part): zoom (1 = the picture just fills the frame) and the position in the spare room (-1..1 each way).
export function cameraAt(motion, u, sceneIndex = 0) {
  const kind = motion && motion !== "auto" ? motion : MOTIONS[sceneIndex % MOTIONS.length];
  const e = easeInOut(clamp(u, 0, 1));
  switch (kind) {
    case "zoom-in": return { zoom: 1.02 + 0.14 * e, x: 0, y: 0 };
    case "zoom-out": return { zoom: 1.16 - 0.14 * e, x: 0, y: 0 };
    case "pan-left": return { zoom: 1.14, x: 0.9 - 1.8 * e, y: 0 };
    case "pan-right": return { zoom: 1.14, x: -0.9 + 1.8 * e, y: 0 };
    case "rise": return { zoom: 1.14, x: 0, y: 0.9 - 1.8 * e };
    case "drift": return { zoom: 1.04 + 0.09 * e, x: -0.5 + 1.0 * e, y: 0.4 - 0.8 * e };
    default: return { zoom: 1.04, x: 0, y: 0 };
  }
}

// The part of a picture (iw x ih) to show in a W x H frame, as [sx, sy, sw, sh]: it fills the frame, zoomed in by `zoom`, moved by x / y.
export function cropFor(iw, ih, W, H, { zoom = 1, x = 0, y = 0 } = {}) {
  const frame = W / H;
  let sw = iw;
  let sh = ih;
  if (iw / ih > frame) sw = ih * frame;
  else sh = iw / frame;
  sw /= zoom;
  sh /= zoom;
  const sx = (iw - sw) / 2 + x * ((iw - sw) / 2);
  const sy = (ih - sh) / 2 + y * ((ih - sh) / 2);
  return [clamp(sx, 0, iw - sw), clamp(sy, 0, ih - sh), sw, sh];
}

// Subtitles: the narration cut into short lines, each shown for a share of the narration time that matches its length.
export function subtitleChunks(text, seconds, maxWords = 8) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length || !(seconds > 0)) return [];
  const sentences = String(text).trim().split(/(?<=[.!?;:؟۔।])\s+/);
  const lines = [];
  sentences.forEach((sentence) => {
    const w = sentence.split(/\s+/).filter(Boolean);
    const parts = Math.max(1, Math.ceil(w.length / maxWords));
    const size = Math.ceil(w.length / parts);
    for (let i = 0; i < w.length; i += size) lines.push(w.slice(i, i + size).join(" "));
  });
  const weights = lines.map((l) => l.length + 6);
  const total = weights.reduce((a, b) => a + b, 0);
  let at = 0;
  return lines.map((line, i) => {
    const length = (weights[i] / total) * seconds;
    const chunk = { text: line, from: at, to: at + length };
    at += length;
    return chunk;
  });
}

// Words wrapped to a width; `measure(text)` gives the width of a piece of text.
export function wrapText(measure, text, maxWidth) {
  const lines = [];
  let line = "";
  String(text || "").split(/\s+/).filter(Boolean).forEach((word) => {
    const trial = line ? `${line} ${word}` : word;
    if (measure(trial) <= maxWidth || !line) line = trial;
    else { lines.push(line); line = word; }
  });
  if (line) lines.push(line);
  return lines;
}

export const SIZES = {
  "16:9": { "720p": [1280, 720], "1080p": [1920, 1080] },
  "9:16": { "720p": [720, 1280], "1080p": [1080, 1920] },
  "1:1": { "720p": [720, 720], "1080p": [1080, 1080] },
};

// The video formats this browser can record, best first: [{ mime, label, ext }]
export function recordableFormats(recorder = typeof MediaRecorder !== "undefined" ? MediaRecorder : null) {
  if (!recorder || typeof recorder.isTypeSupported !== "function") return [];
  const options = [
    ["video/mp4;codecs=avc1.640028,mp4a.40.2", "MP4 (plays everywhere)", "mp4"],
    ["video/mp4;codecs=avc1,mp4a.40.2", "MP4 (plays everywhere)", "mp4"],
    ["video/mp4", "MP4 (plays everywhere)", "mp4"],
    ["video/webm;codecs=vp9,opus", "WebM (VP9)", "webm"],
    ["video/webm;codecs=vp8,opus", "WebM (VP8)", "webm"],
    ["video/webm", "WebM", "webm"],
  ];
  const found = [];
  options.forEach(([mime, label, ext]) => { if (recorder.isTypeSupported(mime) && !found.some((f) => f.ext === ext)) found.push({ mime, label, ext }); });
  return found;
}
